import React, { useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Placeholder from '@tiptap/extension-placeholder';
import { 
  Bold, 
  Italic, 
  Underline as UnderlineIcon, 
  List, 
  ListOrdered, 
  Quote, 
  Heading1, 
  Heading2, 
  Undo, 
  Redo,
  Code,
  AlignLeft,
  AlignCenter,
  AlignRight,
  AlignJustify,
  MoveVertical,
  BetweenVerticalEnd
} from 'lucide-react';

export type LineSpacingOption = 'tight' | 'normal' | 'relaxed' | 'loose';
export type ParaSpacingOption = 'compact' | 'normal' | 'relaxed' | 'spacious';

interface RichTextEditorProps {
  content: string;
  onChange: (content: string) => void;
  placeholder?: string;
  lineSpacing?: LineSpacingOption;
  paraSpacing?: ParaSpacingOption;
  onChangeLineSpacing?: (spacing: LineSpacingOption) => void;
  onChangeParaSpacing?: (spacing: ParaSpacingOption) => void;
}

const MenuBar = ({ 
  editor,
  lineSpacing,
  paraSpacing,
  onChangeLineSpacing,
  onChangeParaSpacing,
}: { 
  editor: any;
  lineSpacing: LineSpacingOption;
  paraSpacing: ParaSpacingOption;
  onChangeLineSpacing: (spacing: LineSpacingOption) => void;
  onChangeParaSpacing: (spacing: ParaSpacingOption) => void;
}) => {
  if (!editor) {
    return null;
  }

  const buttons = [
    {
      icon: <Bold className="w-4 h-4" />,
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: editor.isActive('bold'),
      label: 'Bold',
    },
    {
      icon: <Italic className="w-4 h-4" />,
      action: () => editor.chain().focus().toggleItalic().run(),
      isActive: editor.isActive('italic'),
      label: 'Italic',
    },
    {
      icon: <UnderlineIcon className="w-4 h-4" />,
      action: () => editor.chain().focus().toggleUnderline().run(),
      isActive: editor.isActive('underline'),
      label: 'Underline',
    },
    {
      icon: <Code className="w-4 h-4" />,
      action: () => editor.chain().focus().toggleCode().run(),
      isActive: editor.isActive('code'),
      label: 'Code',
    },
    {
      icon: <Heading1 className="w-4 h-4" />,
      action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: editor.isActive('heading', { level: 1 }),
      label: 'H1',
    },
    {
      icon: <Heading2 className="w-4 h-4" />,
      action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: editor.isActive('heading', { level: 2 }),
      label: 'H2',
    },
    {
      icon: <List className="w-4 h-4" />,
      action: () => editor.chain().focus().toggleBulletList().run(),
      isActive: editor.isActive('bulletList'),
      label: 'Bullet List',
    },
    {
      icon: <ListOrdered className="w-4 h-4" />,
      action: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: editor.isActive('orderedList'),
      label: 'Ordered List',
    },
    {
      icon: <Quote className="w-4 h-4" />,
      action: () => editor.chain().focus().toggleBlockquote().run(),
      isActive: editor.isActive('blockquote'),
      label: 'Quote',
    },
  ];

  const alignmentButtons = [
    {
      icon: <AlignLeft className="w-4 h-4" />,
      action: () => editor.chain().focus().setTextAlign('left').run(),
      isActive: editor.isActive({ textAlign: 'left' }),
      label: 'Align Left',
    },
    {
      icon: <AlignCenter className="w-4 h-4" />,
      action: () => editor.chain().focus().setTextAlign('center').run(),
      isActive: editor.isActive({ textAlign: 'center' }),
      label: 'Align Center',
    },
    {
      icon: <AlignRight className="w-4 h-4" />,
      action: () => editor.chain().focus().setTextAlign('right').run(),
      isActive: editor.isActive({ textAlign: 'right' }),
      label: 'Align Right',
    },
    {
      icon: <AlignJustify className="w-4 h-4" />,
      action: () => editor.chain().focus().setTextAlign('justify').run(),
      isActive: editor.isActive({ textAlign: 'justify' }),
      label: 'Justify',
    },
  ];

  return (
    <div className="flex flex-nowrap md:flex-wrap items-center gap-1.5 p-2 bg-slate-100 sticky top-0 z-30 text-xs rounded-2xl mb-6 overflow-x-auto scrollbar-none max-w-full whitespace-nowrap">
      {/* Basic Text Formatting */}
      <div className="flex items-center gap-0.5 shrink-0">
        {buttons.map((btn, i) => (
          <button
            key={`toolbar-btn-${btn.label}-${i}`}
            type="button"
            onClick={btn.action}
            className={`p-1.5 sm:p-2 rounded-md transition-colors cursor-pointer shrink-0 ${
              btn.isActive 
                ? 'bg-white text-indigo-700 font-bold shadow-2xs' 
                : 'hover:bg-slate-200/80 text-slate-600'
            }`}
            title={btn.label}
          >
            {btn.icon}
          </button>
        ))}
      </div>
      
      <div className="w-px h-5 bg-slate-300/60 mx-1 self-center shrink-0" />

      {/* Alignment Buttons */}
      <div className="flex items-center gap-0.5 shrink-0">
        {alignmentButtons.map((btn, i) => (
          <button
            key={`align-btn-${btn.label}-${i}`}
            type="button"
            onClick={btn.action}
            className={`p-1.5 sm:p-2 rounded-md transition-colors cursor-pointer shrink-0 ${
              btn.isActive 
                ? 'bg-white text-indigo-700 font-bold shadow-2xs' 
                : 'hover:bg-slate-200/80 text-slate-600'
            }`}
            title={btn.label}
          >
            {btn.icon}
          </button>
        ))}
      </div>

      <div className="w-px h-5 bg-slate-300/60 mx-1 self-center shrink-0" />

      {/* Spasi Baris (Line Spacing) Selector */}
      <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-lg shadow-2xs shrink-0" title="Spasi Baris (Line Spacing)">
        <MoveVertical className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
        <span className="text-[10px] font-bold text-slate-500 hidden md:inline">Spasi Baris:</span>
        <select
          value={lineSpacing}
          onChange={(e) => onChangeLineSpacing(e.target.value as LineSpacingOption)}
          className="text-[11px] font-medium bg-transparent text-slate-700 outline-none cursor-pointer"
          title="Atur Spasi Baris (Line Height)"
        >
          <option value="tight">Rapat (1.35x)</option>
          <option value="normal">Normal (1.65x)</option>
          <option value="relaxed">Renggang (1.95x)</option>
          <option value="loose">Sangat Renggang (2.25x)</option>
        </select>
      </div>

      {/* Jarak Paragraf (Paragraph Spacing) Selector */}
      <div className="flex items-center gap-1 bg-white px-2 py-1 rounded-lg shadow-2xs shrink-0" title="Jarak Paragraf (Paragraph Spacing)">
        <BetweenVerticalEnd className="w-3.5 h-3.5 text-indigo-600 shrink-0" />
        <span className="text-[10px] font-bold text-slate-500 hidden md:inline">Jarak Paragraf:</span>
        <select
          value={paraSpacing}
          onChange={(e) => onChangeParaSpacing(e.target.value as ParaSpacingOption)}
          className="text-[11px] font-medium bg-transparent text-slate-700 outline-none cursor-pointer"
          title="Atur Jarak Antar Paragraf"
        >
          <option value="compact">Ringkas (8px)</option>
          <option value="normal">Normal (16px)</option>
          <option value="relaxed">Renggang (24px)</option>
          <option value="spacious">Sangat Renggang (32px)</option>
        </select>
      </div>

      <div className="w-px h-5 bg-slate-300/60 mx-1 self-center shrink-0" />
      
      {/* Undo & Redo */}
      <div className="flex items-center gap-0.5 shrink-0">
        <button
          type="button"
          onClick={() => editor.chain().focus().undo().run()}
          disabled={!editor.can().chain().focus().undo().run()}
          className="p-1.5 sm:p-2 rounded-md hover:bg-slate-200 text-slate-600 disabled:opacity-30 cursor-pointer shrink-0"
          title="Urungkan (Undo)"
        >
          <Undo className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().redo().run()}
          disabled={!editor.can().chain().focus().redo().run()}
          className="p-1.5 sm:p-2 rounded-md hover:bg-slate-200 text-slate-600 disabled:opacity-30 cursor-pointer shrink-0"
          title="Ulangi (Redo)"
        >
          <Redo className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export const RichTextEditor: React.FC<RichTextEditorProps> = ({ 
  content, 
  onChange,
  placeholder,
  lineSpacing: propLineSpacing,
  paraSpacing: propParaSpacing,
  onChangeLineSpacing,
  onChangeParaSpacing,
}) => {
  const [internalLineSpacing, setInternalLineSpacing] = useState<LineSpacingOption>('normal');
  const [internalParaSpacing, setInternalParaSpacing] = useState<ParaSpacingOption>('normal');

  const activeLineSpacing = propLineSpacing ?? internalLineSpacing;
  const activeParaSpacing = propParaSpacing ?? internalParaSpacing;

  const handleLineSpacingChange = (spacing: LineSpacingOption) => {
    if (onChangeLineSpacing) {
      onChangeLineSpacing(spacing);
    } else {
      setInternalLineSpacing(spacing);
    }
  };

  const handleParaSpacingChange = (spacing: ParaSpacingOption) => {
    if (onChangeParaSpacing) {
      onChangeParaSpacing(spacing);
    } else {
      setInternalParaSpacing(spacing);
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Placeholder.configure({
        placeholder: placeholder || 'Mulai ketik naskah gagasan Anda di sini...',
      }),
    ],
    content,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `blog-prose blog-dropcap focus:outline-none min-h-[400px] sm:min-h-[550px] py-4 px-0 transition-all duration-150`,
      },
    },
  });

  return (
    <div className="w-full transition-all">
      <MenuBar
        editor={editor}
        lineSpacing={activeLineSpacing}
        paraSpacing={activeParaSpacing}
        onChangeLineSpacing={handleLineSpacingChange}
        onChangeParaSpacing={handleParaSpacingChange}
      />
      <div className={`line-spacing-${activeLineSpacing} para-spacing-${activeParaSpacing}`}>
        <EditorContent editor={editor} />
      </div>
    </div>
  );
};
